import { TypographyControls, typographyLabel } from "./TypographyControls";
import { describeSyntaxProblem, parseJson } from "./json-import/diagnostics";
import { TextStyles } from "./TextStyles";
import { FlowRegions } from "./FlowRegions";
import { LayoutGroups } from "./LayoutGroups";
import { InsetsControl } from "./InsetsControl";
import { PageMarks } from "./PageMarks";
import { PdfArtBox } from "./PdfArtBox";
import { ImagePlacement } from "./ImagePlacement";
import { PdfArtwork } from "./PdfArtwork";
import { VectorPath } from "./VectorPath";
import { TextTemplateEditor } from "./TextTemplateEditor";
import { CalculationEditor } from "./CalculationEditor";
import { KerningPairs } from "./KerningPairs";
import { CopyfitControls } from "./CopyfitControls";
import { TabStops } from "./TabStops";
import { useAuthoringServices } from "./services";

import { useState, useEffect, useRef } from "react";
import {
  Type,
  Image,
  Square,
  Minus,
  QrCode,
  Plus,
  Copy,
  Trash2,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Grid3X3,
  Move,
  ChevronDown,
  Undo2,
  Redo2,
  ArrowUp,
  ArrowDown,
  CalendarDays,
  GripVertical,
  ExternalLink,
} from "lucide-react";
import {
  calendarAlignment,
  movementLayoutGroup,
  layoutGroupMemberIds,
  translatedLayoutGroups,
  effectiveNode,
  effectiveBackground,
  patchNodeTypography,
  detachTextStyle,
  snapshotFont,
  type FontFaceDescriptor,
  newNode,
  newPage,
  uid,
  parseProject,
  replacePdfArtwork,
  shapeFrame,
  cropPageToShape,
  uploadBaseDocument,
  resolve,
  condition,
  displayValue,
  evaluateCalculation,
  type Project,
  type DesignNode,
  type Page,
  type Values,
  type ScenePage,
} from "@proof/core";
import { ProofCanvas } from "@proof/ui";
import { Select, Control, Toggle, Button, JsonEditor } from "./components";
import { projectEditorFields } from "./project-editor-fields";
import type { EditorProps } from "./Configuration";
export function Designer({
  project: p,
  onChange,
  notify,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onTryItOut,
}: {
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onTryItOut?: (project: Project, signal: AbortSignal) => void | Promise<void>;
} & EditorProps) {
  const [pageIndex, setPageIndex] = useState(0),
    [selectedId, setSelected] = useState(""),
    [grid, setGrid] = useState(false),
    [zoomChoice, setZoom] = useState<number | "fit">("fit"),
    [panel, setPanel] = useState("layer");
  const [showBleed, setShowBleed] = useState(
    p.settings.bleedPreview?.enabled !== false &&
      !!p.settings.bleedPreview?.defaultVisible,
  );
  useEffect(
    () =>
      setShowBleed(
        p.settings.bleedPreview?.enabled !== false &&
          !!p.settings.bleedPreview?.defaultVisible,
      ),
    [
      p.id,
      p.settings.bleedPreview?.enabled,
      p.settings.bleedPreview?.defaultVisible,
    ],
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const artboardRef = useRef<HTMLDivElement>(null);
  const [artboardSize, setArtboardSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const root = rootRef.current;
    const artboard = artboardRef.current;
    if (!root || !artboard) return;
    const fit = () => {
      root.style.height = matchMedia("(max-width: 1000px)").matches
        ? ""
        : `${Math.max(480, window.innerHeight - root.getBoundingClientRect().top)}px`;
    };
    fit();
    const observer = new ResizeObserver(() => {
      fit();
      setArtboardSize({
        width: artboard.clientWidth,
        height: artboard.clientHeight,
      });
    });
    observer.observe(artboard);
    observer.observe(document.body);
    window.addEventListener("resize", fit);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, []);
  const currentLayout = useRef<ScenePage | undefined>(undefined);
  const services = useAuthoringServices();
  const launchTryItOut = onTryItOut || services.tryItOut;
  const [tryingOut, setTryingOut] = useState(false);
  const tryRequest = useRef<AbortController | undefined>(undefined);
  useEffect(() => {
    setTryingOut(false);
    return () => {
      tryRequest.current?.abort();
      tryRequest.current = undefined;
    };
  }, [p.id, launchTryItOut]);
  const tryItOut = async () => {
    if (!launchTryItOut || tryRequest.current) return;
    const controller = new AbortController();
    tryRequest.current = controller;
    setTryingOut(true);
    try {
      // Invoke synchronously within the click so hosts can reserve their popup.
      await launchTryItOut(parseProject(structuredClone(p)), controller.signal);
    } catch (error) {
      if (!controller.signal.aborted) notify((error as Error).message);
    } finally {
      if (tryRequest.current === controller) {
        tryRequest.current = undefined;
        if (!controller.signal.aborted) setTryingOut(false);
      }
    }
  };
  const [importingBase, setImportingBase] = useState(false);
  const baseRequest = useRef<AbortController | undefined>(undefined);
  const latestProject = useRef(p);
  latestProject.current = p;
  useEffect(() => () => baseRequest.current?.abort(), []);
  const [fontCatalog, setFontCatalog] = useState<FontFaceDescriptor[]>([]);
  useEffect(() => {
    let disposed = false;
    setFontCatalog([]);
    if (services.listFonts)
      void services
        .listFonts()
        .then((faces) => {
          if (!disposed) setFontCatalog(faces);
        })
        .catch((e) => {
          if (!disposed) notify(e.message);
        });
    return () => {
      disposed = true;
    };
  }, [services.listFonts]);
  const fontOptions = new Map<string, FontFaceDescriptor>([
    ...p.fontManifest.map((face) => [face.id, face] as const),
    ...fontCatalog.map((face) => [face.id, face] as const),
  ]);
  const page = p.pages[Math.min(pageIndex, p.pages.length - 1)],
    rawNode = page.nodes.find((n) => n.id === selectedId),
    node = rawNode ? effectiveNode(p, rawNode) : undefined;
  const zoom =
    zoomChoice === "fit"
      ? Math.max(
          0.05,
          Math.min(
            (artboardSize.width - 52) / (page.width || 612),
            (artboardSize.height - 140) / (page.height || 792),
          ),
        )
      : zoomChoice;
  const layoutGroups = page.layoutGroups || [];
  const layoutGroup = layoutGroups.find(
    (group) => group.id === node?.layoutGroupId,
  );
  const layerDrag = useRef<
    { projectId: string; pageId: string; id: string; order: string } | undefined
  >(undefined);
  const [layerDrop, setLayerDrop] = useState<{ id: string; after: boolean }>();
  const [layerAnnouncement, setLayerAnnouncement] = useState("");
  const clearLayerDrag = () => {
    layerDrag.current = undefined;
    setLayerDrop(undefined);
  };
  useEffect(clearLayerDrag, [p.id, page.id]);
  const reorderLayer = (id: string, targetId: string, after: boolean) => {
    // The list is front-to-back; stored nodes render back-to-front.
    const ordered = [...page.nodes].reverse();
    const moving = ordered.find((item) => item.id === id);
    if (
      !moving ||
      id === targetId ||
      !ordered.some((item) => item.id === targetId)
    )
      return;
    const movingGroup = movementLayoutGroup(page, id);
    const movingIds = movingGroup
      ? layoutGroupMemberIds(page, movingGroup.id)
      : new Set([id]);
    if (movingIds.has(targetId)) return;
    const targetGroup = movementLayoutGroup(page, targetId);
    const targetIds = targetGroup
      ? layoutGroupMemberIds(page, targetGroup.id)
      : new Set([targetId]);
    const block = ordered.filter((item) => movingIds.has(item.id));
    const rest = ordered.filter((item) => !movingIds.has(item.id));
    const targetIndexes = rest.flatMap((item, index) =>
      targetIds.has(item.id) ? [index] : [],
    );
    const target = after
      ? Math.max(...targetIndexes) + 1
      : Math.min(...targetIndexes);
    rest.splice(target, 0, ...block);
    if (rest.every((item, index) => item.id === ordered[index].id)) return;
    updatePage({ nodes: rest.reverse() });
    setSelected(id);
    setPanel("layer");
    setLayerAnnouncement(
      `${moving.name} moved to layer ${page.nodes.length - rest.findIndex((item) => item.id === id)} of ${page.nodes.length}, counting from the front.`,
    );
  };
  const change = (next: Project) => {
    try {
      onChange(parseProject(next));
    } catch (e) {
      notify((e as Error).message);
    }
  };
  const updatePage = (patch: Partial<Page>) =>
    change({
      ...p,
      pages: p.pages.map((x) =>
        x.id === page.id
          ? {
              ...x,
              ...patch,
              ...(Object.prototype.hasOwnProperty.call(patch, "background") &&
              !patch.backgroundMode
                ? { backgroundMode: "direct" as const }
                : {}),
            }
          : x,
      ),
    });
  const updateNode = (id: string, patch: Partial<DesignNode>) =>
    updatePage({
      nodes: page.nodes.map((n) =>
        n.id === id ? patchNodeTypography(p, n, patch) : n,
      ),
    });
  const add = (type: DesignNode["type"], fieldId?: string) => {
    const n = newNode(type);
    if (["text", "richtext", "calendar"].includes(type)) {
      n.typographyMode = "inherit";
      n.styleId = p.documentSettings.defaultStyleId;
    }
    if (fieldId) {
      n.fieldId = fieldId;
      n.name = p.fields.find((f) => f.id === fieldId)?.label || n.name;
      if (p.fields.find((f) => f.id === fieldId)?.type === "color")
        n.fill = String(
          p.fields.find((f) => f.id === fieldId)?.default || "#000000",
        );
    }
    updatePage({ nodes: [...page.nodes, n] });
    setSelected(n.id);
    setPanel("layer");
  };
  const duplicate = () => {
    if (!rawNode) return;
    const n = {
      ...rawNode,
      id: uid("node"),
      name: `${rawNode.name} copy`,
      x: rawNode.x + 12,
      y: rawNode.y + 12,
    };
    updatePage({ nodes: [...page.nodes, n] });
    setSelected(n.id);
  };
  const remove = () => {
    if (!node) return;
    updatePage({ nodes: page.nodes.filter((n) => n.id !== node.id) });
    setSelected("");
  };
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        (e.target as HTMLElement).closest(
          "input,textarea,select,[contenteditable]",
        )
      )
        return;
      if (
        e.altKey &&
        ["ArrowUp", "ArrowDown"].includes(e.key) &&
        (e.target as HTMLElement).closest(".layer-row")
      )
        return;
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "z") {
          e.preventDefault();
          e.shiftKey ? onRedo() : onUndo();
        }
        if (e.key === "d") {
          e.preventDefault();
          duplicate();
        }
      } else if (node && !node.locked) {
        if (e.key === "Delete" || e.key === "Backspace") {
          e.preventDefault();
          remove();
        }
        if (
          node.placement === "fixed" &&
          ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)
        ) {
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1;
          const movingGroup = movementLayoutGroup(page, node.id);
          if (movingGroup) {
            updatePage({
              layoutGroups: translatedLayoutGroups(
                page,
                movingGroup.id,
                e.key === "ArrowLeft"
                  ? -step
                  : e.key === "ArrowRight"
                    ? step
                    : 0,
                e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0,
              ),
            });
            return;
          }
          if (layoutGroup && layoutGroup.direction !== "overlay") {
            const margins = node.layoutMargins || {
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            };
            updateNode(node.id, {
              layoutMargins: {
                ...margins,
                left: Math.max(
                  0,
                  margins.left +
                    (e.key === "ArrowLeft"
                      ? -step
                      : e.key === "ArrowRight"
                        ? step
                        : 0),
                ),
                top: Math.max(
                  0,
                  margins.top +
                    (e.key === "ArrowUp"
                      ? -step
                      : e.key === "ArrowDown"
                        ? step
                        : 0),
                ),
              },
            });
            return;
          }
          updateNode(node.id, {
            x:
              node.x +
              (e.key === "ArrowLeft"
                ? -step
                : e.key === "ArrowRight"
                  ? step
                  : 0),
            y:
              node.y +
              (e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0),
          });
        }
      }
    };
    const target = rootRef.current;
    target?.addEventListener("keydown", handler);
    return () => target?.removeEventListener("keydown", handler);
  }, [p, node, onUndo, onRedo]);
  return (
    <div
      ref={rootRef}
      tabIndex={0}
      className="designer-workspace"
      onPointerDownCapture={(e) => {
        if ((e.target as Element).closest(".designer-artboard"))
          rootRef.current?.focus({ preventScroll: true });
      }}
    >
      <aside className="designer-layers">
        <header>
          <h3>
            Layers <span className="count">{page.nodes.length}</span>
          </h3>
          <div className="designer-page-select">
            <Select
              aria-label="Designer page"
              value={page.id}
              onChange={(e) => {
                setPageIndex(p.pages.findIndex((x) => x.id === e.target.value));
                setSelected("");
              }}
            >
              {p.pages.map((pg, i) => (
                <option key={pg.id} value={pg.id}>
                  {i + 1}. {pg.name}
                </option>
              ))}
            </Select>
            <button
              className="icon-button"
              aria-label="Add page"
              onClick={() => {
                const pg = { ...newPage(), backgroundMode: "inherit" as const };
                pg.name = `Page ${p.pages.length + 1}`;
                change({ ...p, pages: [...p.pages, pg] });
                setPageIndex(p.pages.length);
              }}
            >
              <Plus size={15} />
            </button>
          </div>
        </header>
        <p className="muted" style={{ padding: "0 16px", fontSize: 12 }}>
          Drag to reorder. Top layers appear in front. Alt + ↑ / ↓ moves a
          focused layer.
        </p>
        <span role="status" className="sr-only">
          {layerAnnouncement}
        </span>
        <div
          className="layer-list"
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null))
              setLayerDrop(undefined);
          }}
        >
          {[...page.nodes].reverse().map((n) => (
            <button
              key={n.id}
              className={`layer-row ${selectedId === n.id ? "selected" : ""}`}
              type="button"
              draggable
              data-layer-id={n.id}
              title={`Drag ${n.name} to change layer order, or use Alt + Up / Down`}
              style={{
                cursor: "grab",
                ...(layerDrop?.id === n.id
                  ? {
                      boxShadow: `inset 0 ${layerDrop.after ? "-3px" : "3px"} 0 var(--color-primary, #287d66)`,
                    }
                  : {}),
              }}
              onDragStart={(e) => {
                layerDrag.current = {
                  projectId: p.id,
                  pageId: page.id,
                  id: n.id,
                  order: JSON.stringify(page.nodes.map((item) => item.id)),
                };
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("application/x-proof-layer", n.id);
                setSelected(n.id);
                setPanel("layer");
              }}
              onDragOver={(e) => {
                const drag = layerDrag.current;
                if (
                  !drag ||
                  drag.projectId !== p.id ||
                  drag.pageId !== page.id ||
                  drag.id === n.id
                )
                  return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                const bounds = e.currentTarget.getBoundingClientRect();
                setLayerDrop({
                  id: n.id,
                  after: e.clientY > bounds.top + bounds.height / 2,
                });
              }}
              onDrop={(e) => {
                const drag = layerDrag.current;
                e.preventDefault();
                if (
                  drag &&
                  drag.projectId === p.id &&
                  drag.pageId === page.id &&
                  drag.order ===
                    JSON.stringify(page.nodes.map((item) => item.id))
                ) {
                  const bounds = e.currentTarget.getBoundingClientRect();
                  reorderLayer(
                    drag.id,
                    n.id,
                    e.clientY > bounds.top + bounds.height / 2,
                  );
                }
                clearLayerDrag();
              }}
              onDragEnd={clearLayerDrag}
              onKeyDown={(e) => {
                if (!e.altKey || !["ArrowUp", "ArrowDown"].includes(e.key))
                  return;
                e.preventDefault();
                e.stopPropagation();
                const ordered = [...page.nodes].reverse(),
                  index = ordered.findIndex((item) => item.id === n.id);
                const target = ordered[index + (e.key === "ArrowUp" ? -1 : 1)];
                if (target)
                  reorderLayer(n.id, target.id, e.key === "ArrowDown");
              }}
              onClick={() => {
                setSelected(n.id);
                setPanel("layer");
              }}
            >
              <span
                aria-hidden="true"
                style={{ flexShrink: 0, display: "flex" }}
              >
                <GripVertical size={13} />
              </span>
              <span className="layer-type">
                {n.type === "text"
                  ? "T"
                  : n.type === "image"
                    ? "▧"
                    : n.type === "qr"
                      ? "▦"
                      : "□"}
              </span>
              <span>
                <strong>{n.name}</strong>
                <small>
                  {n.fieldId
                    ? p.fields.find((f) => f.id === n.fieldId)?.key
                    : n.type}
                </small>
              </span>
              {n.locked ? (
                <Lock size={12} />
              ) : n.hidden ? (
                <EyeOff size={12} />
              ) : null}
            </button>
          ))}
        </div>
        <footer>
          <span className="eyebrow">LINK A VARIABLE</span>
          <Select
            aria-label="Add field layer"
            value=""
            onChange={(e) => {
              const f = p.fields.find((f) => f.id === e.target.value)!;
              add(
                f.type === "qr"
                  ? "qr"
                  : f.type === "color"
                    ? "rect"
                    : f.type === "image" || f.type === "image-list"
                      ? "image"
                      : f.type === "calendar"
                        ? "calendar"
                        : f.type === "map"
                          ? "map"
                          : f.type === "richtext"
                            ? "richtext"
                            : "text",
                f.id,
              );
            }}
          >
            <option value="">+ Add variable to design</option>
            {p.fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </Select>
        </footer>
      </aside>
      <main className="designer-stage">
        <div className="design-toolbar">
          <label
            className="button"
            title="Replace document configuration and pages with a base project. Undo restores the previous document."
          >
            {importingBase ? "Importing base…" : "Import base project"}
            <input
              className="sr-only"
              aria-label="Import base project"
              type="file"
              accept=".json,.pdf,.idml,.indd,.indt,.zip,.png,.jpg,.jpeg,.webp,.tif,.tiff"
              disabled={importingBase}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                const controller = new AbortController();
                baseRequest.current = controller;
                setImportingBase(true);
                try {
                  if (file.size > 50000000)
                    throw new Error("Base project exceeds 50 MB");
                  const base = parseProject(
                    file.name.toLowerCase().endsWith(".json")
                      ? await (async () => {
                          const parsed = parseJson(await file.text());
                          if (!parsed.ok)
                            throw new Error(
                              `${file.name} isn't valid JSON. ${describeSyntaxProblem(parsed.problem)}`,
                            );
                          return parsed.value;
                        })()
                      : await (services.importBase
                          ? services.importBase(file, controller.signal)
                          : Promise.reject(
                              new Error(
                                "Provide an importBase service to import artwork",
                              ),
                            )),
                  );
                  if (controller.signal.aborted) return;
                  if (latestProject.current !== p)
                    throw new Error(
                      "Document changed during import. Import the base again to apply it to the current draft.",
                    );
                  if (base.tenantId !== p.tenantId)
                    throw new Error(
                      "Base project must belong to this workspace",
                    );
                  change({
                    ...base,
                    id: p.id,
                    tenantId: p.tenantId,
                    name: p.name,
                    revision: p.revision,
                    status: p.status,
                  });
                  setPageIndex(0);
                  setSelected("");
                  setPanel("page");
                  notify(
                    base.legacy?.compositionSource
                      ? "Imported publishing package in preview mode. Review layout fidelity before use. Undo restores the previous document."
                      : "Imported base project. Undo restores the previous document.",
                  );
                } catch (e) {
                  if (!controller.signal.aborted) notify((e as Error).message);
                } finally {
                  if (!controller.signal.aborted) setImportingBase(false);
                }
              }}
            />
          </label>
          <div className="tool-cluster">
            {(
              [
                { type: "text", label: "Text", Icon: Type },
                { type: "image", label: "Image", Icon: Image },
                { type: "rect", label: "Shape", Icon: Square },
                { type: "path", label: "Vector", Icon: Square },
                { type: "line", label: "Rule", Icon: Minus },
                { type: "qr", label: "QR", Icon: QrCode },
                { type: "calendar", label: "Calendar", Icon: CalendarDays },
              ] as const
            ).map(({ type, label, Icon }) => (
              <button
                key={type}
                title={`Add ${label}`}
                aria-label={`Add ${label}`}
                onClick={() => add(type)}
              >
                <Icon size={17} />
                <span>{label}</span>
              </button>
            ))}
          </div>
          <div className="tool-cluster">
            {launchTryItOut && (
              <Button
                disabled={tryingOut}
                onClick={() => void tryItOut()}
                title="Open a new proofing window using impersonation"
              >
                <ExternalLink size={17} />
                {tryingOut ? "Opening preview…" : "Try it out"}
              </Button>
            )}
            <button aria-label="Undo" disabled={!canUndo} onClick={onUndo}>
              <Undo2 size={17} />
            </button>
            <button aria-label="Redo" disabled={!canRedo} onClick={onRedo}>
              <Redo2 size={17} />
            </button>
            <button
              title="Snap to grid"
              className={grid ? "active" : ""}
              onClick={() => setGrid(!grid)}
            >
              <Grid3X3 size={17} />
            </button>
            {p.settings.bleedPreview?.enabled !== false && (
              <label className="bleed-preview-toggle">
                <input
                  type="checkbox"
                  checked={showBleed}
                  onChange={(e) => setShowBleed(e.target.checked)}
                />
                Show bleed
              </label>
            )}
            <Select
              aria-label="Designer zoom"
              value={zoomChoice}
              onChange={(e) =>
                setZoom(
                  e.target.value === "fit" ? "fit" : Number(e.target.value),
                )
              }
            >
              <option value="fit">Fit</option>
              {[0.5, 0.75, 1, 1.25, 1.5, 2].map((z) => (
                <option key={z} value={z}>
                  {Math.round(z * 100)}%
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="designer-artboard" ref={artboardRef}>
          <div className="artboard-caption">
            <span>{page.name}</span>
            <span>
              {page.width === null ? "Auto" : `${Math.round(page.width)} pt`} ×{" "}
              {page.height === null ? "Auto" : `${Math.round(page.height)} pt`}
            </span>
          </div>
          <ProofCanvas
            project={p}
            values={{}}
            assetUrl={services.assetUrl}
            loadAsset={services.loadAsset}
            transformImage={services.transformImage}
            loadFont={services.loadFont}
            onStatus={services.onProofStatus}
            sourcePageId={page.id}
            zoom={zoom}
            designer
            showProduction={showBleed}
            selectedId={selectedId}
            grid={grid}
            onSelect={(id) => {
              setSelected(id);
              if (id) setPanel("layer");
            }}
            onLayout={(layout) => {
              currentLayout.current = layout;
            }}
            onGroupMove={(groupId, dx, dy) =>
              updatePage({
                layoutGroups: translatedLayoutGroups(page, groupId, dx, dy),
              })
            }
            onNodeChange={(id, patch) =>
              updateNode(id, {
                ...patch,
                ...(patch.x !== undefined || patch.y !== undefined
                  ? { layoutGroupId: undefined }
                  : {}),
                ...(patch.height !== undefined ? { contentHeight: false } : {}),
              })
            }
          />
          <p className="canvas-hint">
            <Move size={12} /> Drag to position · Arrow keys to nudge · Shift
            for 10 pt · Ctrl+Z to undo
          </p>
        </div>
        <div className="page-strip">
          {p.pages.map((pg, i) => (
            <button
              key={pg.id}
              className={i === pageIndex ? "active" : ""}
              onClick={() => {
                setPageIndex(i);
                setSelected("");
              }}
            >
              {String(i + 1).padStart(2, "0")} {pg.name}
            </button>
          ))}
        </div>
      </main>
      <aside className="designer-inspector">
        <div className="inspector-tabs">
          <button
            className={panel === "layer" ? "active" : ""}
            onClick={() => setPanel("layer")}
          >
            Layer
          </button>
          <button
            className={panel === "page" ? "active" : ""}
            onClick={() => setPanel("page")}
          >
            Page
          </button>
          <button
            className={panel === "styles" ? "active" : ""}
            onClick={() => setPanel("styles")}
          >
            Styles
          </button>
        </div>
        <div className="inspector-body">
          {panel === "layer" && node ? (
            <>
              <Control label="Layer name">
                <input
                  value={node.name}
                  onChange={(e) =>
                    updateNode(node.id, { name: e.target.value })
                  }
                />
              </Control>
              <div className="layer-actions">
                <button
                  title={node.locked ? "Unlock layer" : "Lock layer"}
                  className="icon-button"
                  onClick={() => updateNode(node.id, { locked: !node.locked })}
                >
                  {node.locked ? <Lock size={16} /> : <Unlock size={16} />}
                </button>
                <button
                  title="Toggle layer visibility"
                  className="icon-button"
                  onClick={() => updateNode(node.id, { hidden: !node.hidden })}
                >
                  {node.hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button
                  aria-label="Duplicate layer"
                  className="icon-button"
                  onClick={duplicate}
                >
                  <Copy size={16} />
                </button>
                <button
                  aria-label="Delete layer"
                  className="icon-button danger"
                  onClick={remove}
                >
                  <Trash2 size={16} />
                </button>
                <button
                  title="Bring forward"
                  className="icon-button"
                  onClick={() => {
                    const nodes = [...page.nodes],
                      i = nodes.findIndex((n) => n.id === node.id);
                    if (i < nodes.length - 1)
                      [nodes[i], nodes[i + 1]] = [nodes[i + 1], nodes[i]];
                    updatePage({ nodes });
                  }}
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  title="Send backward"
                  className="icon-button"
                  onClick={() => {
                    const nodes = [...page.nodes],
                      i = nodes.findIndex((n) => n.id === node.id);
                    if (i > 0)
                      [nodes[i], nodes[i - 1]] = [nodes[i - 1], nodes[i]];
                    updatePage({ nodes });
                  }}
                >
                  <ArrowDown size={16} />
                </button>
              </div>
              <h4>
                Position & size <span>pt</span>
              </h4>
              <div className="control-grid">
                {(
                  ["x", "y", "width", "height", "rotation", "opacity"] as const
                ).map((k) => (
                  <Control key={k} label={k}>
                    <input
                      type="number"
                      disabled={
                        (!!layoutGroup &&
                          layoutGroup.direction !== "overlay" &&
                          (k === "x" || k === "y")) ||
                        (node.placement === "flow" &&
                          (k === "x" ||
                            k === "y" ||
                            (k === "width" &&
                              node.flowWidth === "region" &&
                              !!node.flowRegionId &&
                              node.flowRegionId !== "body")))
                      }
                      step={k === "opacity" ? 0.05 : 1}
                      min={
                        k === "width" || k === "height"
                          ? 1
                          : k === "opacity"
                            ? 0
                            : undefined
                      }
                      max={k === "opacity" ? 1 : undefined}
                      value={node[k]}
                      onChange={(e) =>
                        updateNode(node.id, { [k]: Number(e.target.value) })
                      }
                    />
                  </Control>
                ))}
              </div>
              <Control label="Linked variable">
                <Select
                  value={node.fieldId || ""}
                  onChange={(e) =>
                    updateNode(node.id, {
                      fieldId: e.target.value || undefined,
                      textTemplate: undefined,
                      textCalculation: undefined,
                    })
                  }
                >
                  <option value="">
                    {node.textCalculation
                      ? "Calculated text"
                      : node.textTemplate
                        ? "Multiple variables"
                        : "Static content"}
                  </option>
                  {p.fields.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              </Control>
              {node.textTemplate && (
                <TextTemplateEditor
                  project={p}
                  node={node}
                  onChange={(patch) => updateNode(node.id, patch)}
                />
              )}
              {node.textCalculation && (
                <details>
                  <summary>Calculated text</summary>
                  <CalculationEditor
                    value={node.textCalculation}
                    fields={p.fields}
                    label="Layer text"
                    onChange={(textCalculation) =>
                      updateNode(node.id, { textCalculation })
                    }
                  />
                  <Button
                    onClick={() => {
                      try {
                        const resolution = resolve(p);
                        const values = Object.fromEntries(
                          Object.entries(resolution.values).filter(([id]) =>
                            resolution.visible.has(id),
                          ),
                        );
                        const content = displayValue(
                          evaluateCalculation(
                            node.textCalculation!,
                            values,
                            condition,
                            displayValue,
                          ),
                        );
                        updateNode(node.id, {
                          content,
                          textCalculation: undefined,
                        });
                      } catch (error) {
                        notify((error as Error).message);
                      }
                    }}
                  >
                    Use static text
                  </Button>
                </details>
              )}
              {!node.textTemplate &&
                !node.textCalculation &&
                ["text", "richtext"].includes(node.type) && (
                  <Button
                    onClick={() =>
                      updateNode(node.id, {
                        textTemplate: [
                          node.fieldId
                            ? { fieldId: node.fieldId }
                            : { text: node.content },
                        ],
                        fieldId: undefined,
                      })
                    }
                  >
                    Use multiple variables
                  </Button>
                )}
              {!node.fieldId && !node.textTemplate && !node.textCalculation && (
                <Control label="Content">
                  <textarea
                    rows={4}
                    value={node.content}
                    onChange={(e) =>
                      updateNode(node.id, { content: e.target.value })
                    }
                  />
                </Control>
              )}
              {["text", "richtext", "calendar"].includes(node.type) && (
                <>
                  <h4>Typography</h4>
                  <Control label="Text style">
                    <Select
                      value={
                        node.styleId ||
                        (node.typographyMode === "inherit" ? "document" : "")
                      }
                      onChange={(e) => {
                        const choice = e.target.value;
                        updateNode(
                          node.id,
                          choice === "document"
                            ? {
                                styleId: undefined,
                                typographyMode: "inherit",
                                typographyOverrides: {},
                              }
                            : choice
                              ? {
                                  styleId: choice,
                                  typographyMode: "inherit",
                                  typographyOverrides: {},
                                }
                              : detachTextStyle(p, rawNode!),
                        );
                      }}
                    >
                      <option value="">Custom styling</option>
                      <option value="document">Document defaults</option>
                      {p.styles.map((style) => (
                        <option key={style.id} value={style.id}>
                          {style.name}
                        </option>
                      ))}
                    </Select>
                  </Control>
                  <TypographyControls
                    value={{
                      fontFamily: node.fontFamily,
                      fontFaceId: node.fontFaceId || null,
                      fontSize: node.fontSize,
                      fontWeight: node.fontWeight,
                      color: node.color,
                      lineHeight: node.lineHeight,
                      align:
                        node.type === "calendar"
                          ? calendarAlignment(node)
                          : node.align,
                    }}
                    fonts={fontOptions}
                    onChange={(patch) =>
                      updateNode(
                        node.id,
                        node.type === "calendar" && patch.align
                          ? {
                              typographyOverrides: {
                                ...node.typographyOverrides,
                                align: patch.align,
                              },
                            }
                          : (patch as Partial<DesignNode>),
                      )
                    }
                    onFont={(face) => {
                      const next = face
                        ? snapshotFont(p, face, [...fontOptions.values()])
                        : p;
                      const properties = face
                        ? {
                            fontFaceId: face.id,
                            fontFamily: face.family,
                            fontWeight: face.weight,
                          }
                        : {
                            fontFaceId: undefined,
                            fontFamily: "Inter",
                            fontWeight: 400,
                          };
                      change({
                        ...next,
                        pages: next.pages.map((pg) =>
                          pg.id === page.id
                            ? {
                                ...pg,
                                nodes: pg.nodes.map((n) =>
                                  n.id === node.id
                                    ? patchNodeTypography(next, n, properties)
                                    : n,
                                ),
                              }
                            : pg,
                        ),
                      });
                    }}
                  />
                  {["text", "richtext"].includes(node.type) && (
                    <>
                      {(["kerning", "ligatures"] as const).map((feature) => (
                        <Control
                          key={feature}
                          label={
                            feature === "kerning"
                              ? "Text kerning"
                              : "Text ligatures"
                          }
                        >
                          <Select
                            value={
                              node.textFeatures?.[feature] === undefined
                                ? "auto"
                                : node.textFeatures[feature]
                                  ? "on"
                                  : "off"
                            }
                            onChange={(event) => {
                              const textFeatures = { ...node.textFeatures };
                              if (event.target.value === "auto")
                                delete textFeatures[feature];
                              else
                                textFeatures[feature] =
                                  event.target.value === "on";
                              updateNode(node.id, {
                                textFeatures: Object.keys(textFeatures).length
                                  ? textFeatures
                                  : undefined,
                              });
                            }}
                          >
                            <option value="auto">
                              Imported / font default
                            </option>
                            <option value="on">On</option>
                            <option value="off">Off</option>
                          </Select>
                        </Control>
                      ))}
                      <KerningPairs
                        key={node.id}
                        value={node.textFeatures?.pairAdjustments}
                        fonts={p.fontManifest}
                        enabled={!!node.fontFaceId}
                        onChange={(pairAdjustments) => {
                          const textFeatures = { ...node.textFeatures };
                          if (pairAdjustments?.length)
                            textFeatures.pairAdjustments = pairAdjustments;
                          else delete textFeatures.pairAdjustments;
                          updateNode(node.id, {
                            textFeatures: Object.keys(textFeatures).length
                              ? textFeatures
                              : undefined,
                          });
                        }}
                      />
                      <TabStops
                        key={`tabs_${node.id}`}
                        value={node.tabInterval}
                        onChange={(tabInterval) =>
                          updateNode(node.id, { tabInterval })
                        }
                      />
                    </>
                  )}
                  {["text", "richtext"].includes(node.type) && (
                    <Control label="First text baseline">
                      <Select
                        value={node.baselineMode || "em"}
                        onChange={(e) =>
                          updateNode(node.id, {
                            baselineMode: e.target.value as "em" | "ascent",
                            ...(e.target.value === "em"
                              ? { textHeightMode: "line-box" as const }
                              : {}),
                          })
                        }
                      >
                        <option value="em">Font size</option>
                        <option value="ascent">Font ascent</option>
                      </Select>
                    </Control>
                  )}
                  {["text", "richtext"].includes(node.type) && (
                    <Control label="Text baseline shift (pt)">
                      <input
                        type="number"
                        min={-500}
                        max={500}
                        step="any"
                        value={node.textBaselineShift ?? 0}
                        onChange={(event) => {
                          const shift = Number(event.target.value);
                          if (Number.isFinite(shift) && Math.abs(shift) <= 500)
                            updateNode(node.id, {
                              textBaselineShift: shift || undefined,
                            });
                        }}
                      />
                    </Control>
                  )}
                  {["text", "richtext"].includes(node.type) && (
                    <Control label="Vertical text alignment">
                      <Select
                        value={node.verticalAlign || "top"}
                        onChange={(e) =>
                          updateNode(node.id, {
                            verticalAlign: e.target.value as
                              "top" | "middle" | "bottom" | "bottom-baseline",
                          })
                        }
                      >
                        <option value="top">Top</option>
                        <option value="middle">Middle</option>
                        <option value="bottom">Bottom</option>
                        <option value="bottom-baseline">Bottom baseline</option>
                      </Select>
                    </Control>
                  )}
                  {["text", "richtext"].includes(node.type) && (
                    <Toggle
                      label="Allow glyph overhang"
                      value={!!node.allowGlyphOverhang}
                      onChange={(allowGlyphOverhang) =>
                        updateNode(node.id, { allowGlyphOverhang })
                      }
                    />
                  )}
                  {(node.styleId ||
                    node.typographyMode === "inherit" ||
                    Object.keys(node.typographyOverrides).length > 0) && (
                    <>
                      <p className="muted">
                        {Object.keys(node.typographyOverrides).length} layer
                        overrides
                      </p>
                      <div className="button-row">
                        {Object.keys(node.typographyOverrides).map((key) => (
                          <Button
                            key={key}
                            onClick={() => {
                              const overrides = { ...node.typographyOverrides };
                              delete overrides[key as keyof typeof overrides];
                              updateNode(node.id, {
                                typographyOverrides: overrides,
                              });
                            }}
                          >
                            Reset {typographyLabel(key)}
                          </Button>
                        ))}
                      </div>
                      <Button
                        onClick={() =>
                          updateNode(node.id, { typographyOverrides: {} })
                        }
                      >
                        Reset layer typography
                      </Button>
                    </>
                  )}
                </>
              )}
              {node.type === "image" && (
                <>
                  <PdfArtwork
                    key={`${p.id}:${page.id}:${node.id}`}
                    node={node}
                    onApply={(placedPdf, keepPlacement) =>
                      change(
                        replacePdfArtwork(p, page.id, node.id, placedPdf, {
                          keepPlacement,
                        }),
                      )
                    }
                    onRemove={() =>
                      updateNode(node.id, { placedPdf: undefined })
                    }
                  />
                  <ImagePlacement
                    node={node}
                    onChange={(patch) => updateNode(node.id, patch)}
                  />
                  <Toggle
                    label="Clip image to frame"
                    value={!!node.clipImageToFrame}
                    onChange={(clipImageToFrame) =>
                      updateNode(node.id, {
                        clipImageToFrame: clipImageToFrame || undefined,
                      })
                    }
                  />
                </>
              )}
              {node.type === "path" && (
                <VectorPath
                  node={node}
                  onChange={(patch) => updateNode(node.id, patch)}
                />
              )}
              {["rect", "ellipse", "line", "path"].includes(node.type) && (
                <>
                  <h4>Appearance</h4>
                  <Control label="Fill">
                    <input
                      value={node.fill}
                      onChange={(e) =>
                        updateNode(node.id, { fill: e.target.value })
                      }
                    />
                  </Control>
                  <Control label="Stroke">
                    <input
                      value={node.stroke}
                      onChange={(e) =>
                        updateNode(node.id, { stroke: e.target.value })
                      }
                    />
                  </Control>
                  <div className="control-grid">
                    <Control label="Stroke width">
                      <input
                        type="number"
                        min="0"
                        step=".1"
                        value={node.strokeWidth}
                        onChange={(e) =>
                          updateNode(node.id, {
                            strokeWidth: Number(e.target.value),
                          })
                        }
                      />
                    </Control>
                    <Control label="Corner radius">
                      <input
                        type="number"
                        min="0"
                        value={node.radius}
                        onChange={(e) =>
                          updateNode(node.id, {
                            radius: Number(e.target.value),
                          })
                        }
                      />
                    </Control>
                  </div>
                </>
              )}
              <details>
                <summary>Placement & overflow</summary>
                <Control label="Placement">
                  <Select
                    value={node.placement}
                    disabled={!!node.layoutGroupId}
                    onChange={(e) =>
                      updateNode(node.id, {
                        placement: e.target.value as DesignNode["placement"],
                      })
                    }
                  >
                    <option value="fixed">Fixed on page</option>
                    <option value="flow">Flow with content</option>
                  </Select>
                </Control>
                <Control label="Layout group">
                  <Select
                    value={node.layoutGroupId || ""}
                    onChange={(event) => {
                      const id = event.target.value;
                      const placed = currentLayout.current?.nodes.find(
                        (item) => item.sourceId === node.id,
                      );
                      updateNode(node.id, {
                        layoutGroupId: id || undefined,
                        placement: "fixed",
                        anchor: "top-left",
                        stretch: "none",
                        x: id ? 0 : (placed?.x ?? node.x),
                        y: id ? 0 : (placed?.y ?? node.y),
                      });
                    }}
                  >
                    <option value="">Page positioning</option>
                    {layoutGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </Select>
                </Control>
                {layoutGroup && (
                  <>
                    <p className="inspector-note">
                      {layoutGroup.direction === "overlay"
                        ? "Coordinates are relative to this group."
                        : "Group order and margins control position."}{" "}
                      {movementLayoutGroup(page, node.id)
                        ? "Group is locked together: drag any member to move the group."
                        : "Group is unlocked: dragging moves this layer out of the group."}
                    </p>
                    <Toggle
                      label="Lock group together"
                      value={layoutGroup.moveTogether !== false}
                      onChange={(moveTogether) =>
                        updatePage({
                          layoutGroups: layoutGroups.map((group) =>
                            group.id === layoutGroup.id
                              ? { ...group, moveTogether }
                              : group,
                          ),
                        })
                      }
                    />
                    <Control label="Order in layout group">
                      <input
                        type="number"
                        min={-10000}
                        max={10000}
                        disabled={!!movementLayoutGroup(page, node.id)}
                        value={node.layoutOrder || 0}
                        onChange={(event) =>
                          updateNode(node.id, {
                            layoutOrder: Number(event.target.value),
                          })
                        }
                      />
                    </Control>
                    <InsetsControl
                      label="Layer margins"
                      value={node.layoutMargins || 0}
                      onChange={(value) =>
                        updateNode(node.id, {
                          layoutMargins:
                            typeof value === "number"
                              ? {
                                  top: value,
                                  right: value,
                                  bottom: value,
                                  left: value,
                                }
                              : value,
                        })
                      }
                    />
                  </>
                )}
                {["text", "richtext"].includes(node.type) && (
                  <>
                    <Toggle
                      label="Height fits text content"
                      value={!!node.contentHeight}
                      onChange={(contentHeight) =>
                        updateNode(node.id, { contentHeight })
                      }
                    />
                    <Control label="Text height measurement">
                      <Select
                        value={node.textHeightMode || "line-box"}
                        onChange={(event) =>
                          updateNode(node.id, {
                            textHeightMode: event.target
                              .value as DesignNode["textHeightMode"],
                            ...(event.target.value === "font-box"
                              ? { baselineMode: "ascent" as const }
                              : {}),
                          })
                        }
                      >
                        <option value="line-box">Line boxes</option>
                        <option value="font-box">
                          Font ascent and descent
                        </option>
                      </Select>
                    </Control>
                  </>
                )}
                {node.placement === "flow" && (
                  <>
                    <Control label="Flow region">
                      <Select
                        value={node.flowRegionId || "body"}
                        onChange={(e) =>
                          updateNode(node.id, { flowRegionId: e.target.value })
                        }
                      >
                        <option value="body">Page body</option>
                        {page.flowRegions.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </Select>
                    </Control>
                    <Control label="Flow width">
                      <Select
                        value={node.flowWidth}
                        onChange={(e) =>
                          updateNode(node.id, {
                            flowWidth: e.target
                              .value as DesignNode["flowWidth"],
                          })
                        }
                      >
                        <option value="region">Use region width</option>
                        <option value="node">Use layer width</option>
                      </Select>
                    </Control>
                    <div className="control-grid">
                      {(["flowOrder", "minHeight", "maxHeight"] as const).map(
                        (key) => (
                          <Control
                            key={key}
                            label={
                              key === "flowOrder"
                                ? "Flow order"
                                : key === "minHeight"
                                  ? "Minimum flow height (pt)"
                                  : "Maximum flow height (pt)"
                            }
                          >
                            <input
                              type="number"
                              value={node[key]}
                              min={
                                key === "flowOrder"
                                  ? -10000
                                  : key === "maxHeight"
                                    ? 1
                                    : 0
                              }
                              max={14400}
                              onChange={(e) =>
                                updateNode(node.id, {
                                  [key]: Number(e.target.value),
                                })
                              }
                            />
                          </Control>
                        ),
                      )}
                    </div>
                    <Toggle
                      label="Grow to fit content"
                      value={node.flowGrow}
                      onChange={(flowGrow) => updateNode(node.id, { flowGrow })}
                    />
                    <Toggle
                      label="Push following content"
                      value={node.flowPush}
                      onChange={(flowPush) => updateNode(node.id, { flowPush })}
                    />
                    <Toggle
                      label="Use region spacing"
                      value={node.flowGap === undefined}
                      onChange={(inherit) =>
                        updateNode(node.id, {
                          flowGap: inherit ? undefined : 8,
                        })
                      }
                    />
                    {node.flowGap !== undefined && (
                      <Control label="Flow gap (pt)">
                        <input
                          type="number"
                          min={0}
                          max={14400}
                          value={node.flowGap}
                          onChange={(e) =>
                            updateNode(node.id, {
                              flowGap: Number(e.target.value),
                            })
                          }
                        />
                      </Control>
                    )}
                  </>
                )}
                <Control label="Overflow">
                  <Select
                    value={node.overflow}
                    onChange={(e) =>
                      updateNode(node.id, {
                        overflow: e.target.value as DesignNode["overflow"],
                      })
                    }
                  >
                    {["clip", "shrink", "continue"].map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </Select>
                </Control>
                {["text", "richtext"].includes(node.type) &&
                  node.overflow === "shrink" && (
                    <CopyfitControls
                      key={node.id}
                      value={node.copyfit}
                      onChange={(copyfit) => updateNode(node.id, { copyfit })}
                    />
                  )}
                <Control label="Anchor">
                  <Select
                    value={node.anchor}
                    onChange={(e) =>
                      updateNode(node.id, {
                        anchor: e.target.value as DesignNode["anchor"],
                      })
                    }
                  >
                    {[
                      "top-left",
                      "top-right",
                      "bottom-left",
                      "bottom-right",
                      "center",
                    ].map((a) => (
                      <option key={a}>{a}</option>
                    ))}
                  </Select>
                </Control>
                <Control label="Stretch">
                  <Select
                    value={node.stretch}
                    onChange={(e) =>
                      updateNode(node.id, {
                        stretch: e.target.value as DesignNode["stretch"],
                      })
                    }
                  >
                    {["none", "horizontal", "vertical", "both"].map((a) => (
                      <option key={a}>{a}</option>
                    ))}
                  </Select>
                </Control>
              </details>
              <details>
                <summary>Layer visibility rule</summary>
                <JsonEditor
                  label="Layer visibility rule"
                  field={projectEditorFields(p).condition}
                  value={node.visibleWhen || null}
                  onSave={(v) =>
                    updateNode(node.id, {
                      visibleWhen: v
                        ? (v as DesignNode["visibleWhen"])
                        : undefined,
                    })
                  }
                />
              </details>
            </>
          ) : panel === "layer" ? (
            <div className="inspector-empty">
              <Square size={30} />
              <h3>Select a layer</h3>
              <p>
                Click a layer on the canvas or in the layer list to start
                designing.
              </p>
              <Button onClick={() => setPanel("page")}>Page settings</Button>
            </div>
          ) : null}
          {panel === "page" && (
            <>
              <h3>Page settings</h3>
              <Control label="Document frame">
                <Select
                  value={page.frame ? "custom" : "rectangle"}
                  onChange={(e) =>
                    updatePage({
                      frame:
                        e.target.value === "rectangle"
                          ? undefined
                          : shapeFrame(e.target.value as "ellipse" | "rounded"),
                      background: null,
                      backgroundMode: "direct",
                    })
                  }
                >
                  <option value="rectangle">Rectangular page</option>
                  <option value="ellipse">Ellipse / circle</option>
                  <option value="rounded">Rounded rectangle</option>
                  {page.frame && (
                    <option value="custom">{page.frame.name}</option>
                  )}
                </Select>
              </Control>
              <label className="button">
                {importingBase ? "Uploading base…" : "Upload base document"}
                <input
                  className="sr-only"
                  aria-label="Upload base document"
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.tif,.tiff,image/png,image/jpeg,image/webp,image/tiff"
                  disabled={importingBase || !services.importBase}
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (!file) return;
                    const controller = new AbortController();
                    baseRequest.current = controller;
                    setImportingBase(true);
                    try {
                      if (file.size > 50000000)
                        throw new Error("Base image exceeds 50 MB");
                      if (!/\.(png|jpe?g|webp|tiff?)$/i.test(file.name))
                        throw new Error(
                          "Choose a PNG, JPEG, WebP or TIFF image",
                        );
                      const imported = await services.importBase!(
                        file,
                        controller.signal,
                      );
                      if (controller.signal.aborted) return;
                      if (latestProject.current !== p)
                        throw new Error(
                          "Document changed during upload. Upload the base image again to the current draft.",
                        );
                      change(
                        uploadBaseDocument(p, page.id, parseProject(imported)),
                      );
                      setSelected("");
                      notify(
                        "Base image uploaded. This page follows its visible shape and size. Undo restores the previous page.",
                      );
                    } catch (error) {
                      if (!controller.signal.aborted)
                        notify((error as Error).message);
                    } finally {
                      if (!controller.signal.aborted) setImportingBase(false);
                    }
                  }}
                />
              </label>
              <p className="muted">
                Use PNG, JPEG, WebP or TIFF artwork. Transparent edges define
                the document shape; opaque images use their rectangular bounds.
                Existing artwork stays on this page.
              </p>
              <Control label="Crop document to shape">
                <Select
                  value=""
                  onChange={(e) => {
                    try {
                      change(cropPageToShape(p, page.id, e.target.value));
                      setSelected("");
                    } catch (e) {
                      notify((e as Error).message);
                    }
                  }}
                >
                  <option value="">Choose a shape layer</option>
                  {page.nodes
                    .filter((n) => ["rect", "ellipse", "path"].includes(n.type))
                    .map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.name}
                      </option>
                    ))}
                </Select>
              </Control>
              {page.frame && (
                <p className="muted">
                  All artwork is cropped to this frame. Page width and height
                  resize the frame. Transparent formats preserve the outside
                  silhouette.
                </p>
              )}
              <Control label="Page name">
                <input
                  value={page.name}
                  onChange={(e) => updatePage({ name: e.target.value })}
                />
              </Control>
              <Toggle
                label="Auto-size to content"
                value={page.width === null && page.height === null}
                onChange={(v) =>
                  updatePage({ width: v ? null : 360, height: v ? null : 504 })
                }
              />
              <div className="control-grid">
                <Control label="Width (pt)">
                  <input
                    type="number"
                    disabled={page.width === null}
                    value={page.width ?? ""}
                    min="1"
                    max="14400"
                    onChange={(e) =>
                      updatePage({ width: Number(e.target.value) })
                    }
                  />
                </Control>
                <Control label="Height (pt)">
                  <input
                    type="number"
                    disabled={page.height === null}
                    value={page.height ?? ""}
                    min="1"
                    max="14400"
                    onChange={(e) =>
                      updatePage({ height: Number(e.target.value) })
                    }
                  />
                </Control>
              </div>
              <Control label="Size preset">
                <Select
                  value=""
                  onChange={(e) => {
                    const [w, h] = e.target.value.split(",").map(Number);
                    updatePage({ width: w, height: h });
                  }}
                >
                  <option value="">Choose a page size</option>
                  <option value="612,792">US Letter · 8.5 × 11 in</option>
                  <option value="595.28,841.89">A4 · 210 × 297 mm</option>
                  <option value="360,504">Invitation · 5 × 7 in</option>
                  <option value="252,144">Business card · 3.5 × 2 in</option>
                  <option value="432,288">Postcard · 6 × 4 in</option>
                </Select>
              </Control>
              <Toggle
                label="Use document background"
                value={page.backgroundMode === "inherit"}
                onChange={(inherit) =>
                  updatePage({
                    backgroundMode: inherit ? "inherit" : "direct",
                    background: effectiveBackground(p, page),
                  })
                }
              />
              <Toggle
                label="Transparent background"
                value={effectiveBackground(p, page) === null}
                onChange={(v) =>
                  updatePage({ background: v ? null : "#ffffff" })
                }
              />
              {effectiveBackground(p, page) !== null && (
                <Control label="Background color">
                  <input
                    type="color"
                    value={effectiveBackground(p, page) || "#ffffff"}
                    onChange={(e) => updatePage({ background: e.target.value })}
                  />
                </Control>
              )}
              <Control label="Margin (pt)">
                <input
                  type="number"
                  min="0"
                  value={page.margin}
                  onChange={(e) =>
                    updatePage({ margin: Number(e.target.value) })
                  }
                />
              </Control>
              <FlowRegions page={page} onChange={updatePage} />
              <LayoutGroups page={page} onChange={updatePage} />
              <h4>Print geometry</h4>
              <PdfArtBox
                value={page.artBox}
                width={page.width || 612}
                height={page.height || 792}
                onChange={(artBox) => updatePage({ artBox })}
              />
              <InsetsControl
                label="Bleed"
                max={144}
                value={page.production.bleed}
                onChange={(bleed) =>
                  updatePage({ production: { ...page.production, bleed } })
                }
              />
              <InsetsControl
                label="Slug"
                max={144}
                value={page.production.slug}
                onChange={(slug) =>
                  updatePage({ production: { ...page.production, slug } })
                }
              />
              <PageMarks
                value={page.production}
                onChange={(production) => updatePage({ production })}
              />
              <Toggle
                label="Clip artwork to bleed boundary"
                value={page.production.clipArtworkToBleed !== false}
                onChange={(clipArtworkToBleed) =>
                  updatePage({
                    production: { ...page.production, clipArtworkToBleed },
                  })
                }
              />
              <h4>Repeating header & footer</h4>
              <div className="control-grid">
                {(["headerHeight", "footerHeight"] as const).map((key) => (
                  <Control
                    key={key}
                    label={
                      key === "headerHeight"
                        ? "Header height (pt)"
                        : "Footer height (pt)"
                    }
                  >
                    <input
                      type="number"
                      min={0}
                      max={144}
                      value={p.settings[key] ?? p.settings.bandHeight}
                      onChange={(e) =>
                        change({
                          ...p,
                          settings: {
                            ...p.settings,
                            [key]: Number(e.target.value),
                          },
                        })
                      }
                    />
                  </Control>
                ))}
              </div>
              {(["header", "footer"] as const).map((k) => (
                <Control
                  key={k}
                  label={k}
                  note="Tokens: {page}, {pages}, {pageName}"
                >
                  <input
                    value={p.settings[k]}
                    onChange={(e) =>
                      change({
                        ...p,
                        settings: { ...p.settings, [k]: e.target.value },
                      })
                    }
                  />
                </Control>
              ))}
              <Button
                onClick={() => {
                  const pg = {
                    ...structuredClone(page),
                    id: uid("page"),
                    name: `${page.name} copy`,
                    nodes: page.nodes.map((n) => ({ ...n, id: uid("node") })),
                  };
                  change({ ...p, pages: [...p.pages, pg] });
                  setPageIndex(p.pages.length);
                }}
              >
                <Copy size={15} />
                Duplicate page
              </Button>
              <Button
                disabled={p.pages.length === 1}
                kind="danger"
                onClick={() => {
                  change({
                    ...p,
                    pages: p.pages.filter((pg) => pg.id !== page.id),
                  });
                  setPageIndex(0);
                }}
              >
                <Trash2 size={15} />
                Delete page
              </Button>
            </>
          )}
          {panel === "styles" && (
            <TextStyles project={p} fonts={fontOptions} onChange={change} />
          )}
        </div>
      </aside>
    </div>
  );
}
